import { useEffect, useState } from "react";
import { CheckCircle2, Home, Clock, XCircle, Loader2, MessageCircle } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";

/**
 * Iter 137 — Página de retorno tras aprobar el pago en PayPal.
 *
 * PayPal deja la Order en estado `APPROVED` tras la aprobación. El merchant
 * DEBE llamar a `POST /v2/checkout/orders/{ORDER_ID}/capture` para que el
 * cargo se ejecute realmente y se dispare el webhook `PAYMENT.CAPTURE.
 * COMPLETED`. Sin este `capture`, el botón "Pagar ahora" de PayPal queda
 * en loading indefinido.
 *
 * Esta página captura automáticamente al llegar. El estado se actualiza
 * en cuanto PayPal responde COMPLETED. Idempotente PayPal-side (via
 * `PayPal-Request-Id`) — recargar no duplica cargos.
 */
export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const orderId = params.get("token") || params.get("order_id") || "";
  const [state, setState] = useState({ phase: "capturing", data: null, error: "" });

  useEffect(() => {
    if (!orderId) {
      setState({ phase: "no_order", data: null, error: "" });
      return;
    }
    const base = process.env.REACT_APP_BACKEND_URL || "";
    axios.post(`${base}/api/paypal/capture`, { order_id: orderId })
      .then((r) => {
        if (r.data?.ok && r.data?.status === "COMPLETED") {
          setState({ phase: "done", data: r.data, error: "" });
        } else {
          setState({ phase: "pending", data: r.data, error: "" });
        }
      })
      .catch((err) => {
        const msg = err?.response?.data?.detail || err?.message || "Error al confirmar el pago";
        setState({ phase: "error", data: null, error: msg });
      });
  }, [orderId]);

  const isDone = state.phase === "done";
  const isError = state.phase === "error";
  const isPending = state.phase === "pending";
  const isCapturing = state.phase === "capturing";

  const headerColor = isError ? "#B84A2A" : "#6E8B3D";
  const Icon = isError ? XCircle : (isCapturing ? Loader2 : CheckCircle2);

  return (
    <div className="min-h-screen bg-[#F5F5F0] flex items-center justify-center px-4">
      <div className="w-full max-w-md bg-white border border-[#111110] shadow-2xl">
        <div className="px-6 py-4 flex items-center gap-3 text-white" style={{ background: headerColor }}>
          <Icon size={28} className={isCapturing ? "animate-spin" : ""} />
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest opacity-80">iRepairs.es</div>
            <h1 className="font-display font-black text-xl">
              {isCapturing ? "Confirmando pago…" : isError ? "Error de confirmación" : isPending ? "Pago en revisión" : "Pago recibido"}
            </h1>
          </div>
        </div>
        <div className="px-6 py-8 text-center space-y-4">
          {isCapturing && (
            <>
              <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                <Loader2 size={40} className="text-green-700 animate-spin" />
              </div>
              <h2 className="font-display text-2xl font-black">Confirmando tu pago con PayPal…</h2>
              <p className="text-sm text-gray-700">Esto tarda solo unos segundos, no cierres esta ventana.</p>
            </>
          )}

          {isDone && (
            <>
              <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle2 size={40} className="text-green-700" />
              </div>
              <h2 className="font-display text-2xl font-black">¡Pago recibido!</h2>
              <p className="text-sm text-gray-700 leading-relaxed">
                Hemos cobrado <strong>{(state.data.amount_paid || 0).toFixed(2)} €</strong> y hemos
                registrado el pago en tu reparación.
              </p>
              <div className="text-[11px] text-gray-500 font-mono border-t pt-3">
                Ref. captura: <span className="break-all">{state.data.capture_id}</span>
              </div>
            </>
          )}

          {isPending && (
            <>
              <div className="mx-auto w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center">
                <Clock size={40} className="text-amber-700" />
              </div>
              <h2 className="font-display text-2xl font-black">Estamos verificando tu pago</h2>
              <p className="text-sm text-gray-700 leading-relaxed">
                PayPal aún no ha confirmado el pago. Recibirás la confirmación en unos
                minutos y quedará registrado automáticamente en tu reparación.
              </p>
            </>
          )}

          {isError && (
            <>
              <div className="mx-auto w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center">
                <XCircle size={40} className="text-orange-700" />
              </div>
              <h2 className="font-display text-2xl font-black">No hemos podido confirmar el pago</h2>
              <p className="text-sm text-gray-700 leading-relaxed">
                Contacta con nosotros para que verifiquemos manualmente tu pago.
              </p>
              <details className="text-[11px] text-gray-500 mt-2">
                <summary className="cursor-pointer">Detalles técnicos</summary>
                <pre className="mt-2 text-left bg-gray-50 p-2 border overflow-x-auto whitespace-pre-wrap">{state.error}</pre>
              </details>
            </>
          )}

          {orderId && (
            <div className="text-[11px] text-gray-500 font-mono border-t pt-3">
              Ref. PayPal: <span className="break-all">{orderId}</span>
            </div>
          )}
          <div className="pt-2 flex flex-col sm:flex-row items-center justify-center gap-2">
            <a
              href="https://irepairs.es"
              className="inline-flex items-center gap-2 border border-[#111110] hover:bg-[#111110] hover:text-white px-4 py-2 text-sm w-full sm:w-auto justify-center"
              data-testid="payment-success-home-btn"
            >
              <Home size={14} /> Ir a irepairs.es
            </a>
            <a
              href="https://wa.me/34628712498"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1EBE5D] text-white px-4 py-2 text-sm w-full sm:w-auto justify-center"
              data-testid="payment-success-whatsapp-btn"
            >
              <MessageCircle size={14} /> Contactar por WhatsApp
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
